import { BadgeCheck, Ear, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { addFeedbackNotes, submitFeedback } from '~/lib/interview-api';

type FeedbackPhase = 'thumbs' | 'notes' | 'done';

interface CompletionScreenProps {
  jobTitle: string;
  token: string;
  feedbackSubmitted: boolean;
}

export function CompletionScreen({
  token,
  feedbackSubmitted
}: CompletionScreenProps): React.ReactNode {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<FeedbackPhase>(feedbackSubmitted ? 'done' : 'thumbs');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleThumbs = useCallback(
    async (thumbs: 'up' | 'down') => {
      setIsSubmitting(true);
      setError(null);
      try {
        await submitFeedback(token, thumbs);
        setPhase('notes');
      } catch {
        setError(t('screening.feedback.submitError'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [token, t]
  );

  const handleSubmitNotes = useCallback(async () => {
    if (!notesText.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await addFeedbackNotes(token, notesText);
      setPhase('done');
      setSheetOpen(false);
    } catch {
      setError(t('screening.feedback.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  }, [token, notesText, t]);

  const sheetTranslate =
    phase === 'done'
      ? 'translate-y-full'
      : sheetOpen
        ? 'translate-y-0'
        : 'translate-y-[calc(100%-80px)]';

  return (
    <div className="relative min-h-screen overflow-hidden bg-green-600">
      {/* Success content — centered on the green background */}
      <div className="flex min-h-screen flex-col items-center justify-center gap-5 px-8 pb-20 text-white">
        <BadgeCheck className="size-20 stroke-[1.25]" />
        <h1 className="text-center text-2xl font-semibold">{t('screening.completed.submitted')}</h1>
      </div>

      {/* Bottom sheet */}
      <div
        className={`fixed inset-x-0 bottom-0 transition-transform duration-300 ease-out ${sheetTranslate}`}
      >
        {/* Teaser — always visible in the collapsed state */}
        <button
          type="button"
          aria-expanded={sheetOpen}
          className="flex w-full items-center gap-3 rounded-t-3xl bg-card px-5 py-5 shadow-lg"
          onClick={() => {
            setSheetOpen(true);
          }}
        >
          <Ear className="size-5 shrink-0 text-muted-foreground" />
          <div className="text-left">
            <p className="text-xs text-muted-foreground">{t('screening.feedback.helpUsImprove')}</p>
            <p className="text-sm font-medium">{t('screening.feedback.teaser')}</p>
          </div>
        </button>

        {/* Sheet body */}
        <div className="bg-card px-5 pb-10">
          {phase === 'thumbs' && (
            <div className="flex flex-col gap-5 pb-2 pt-1">
              <h2 className="text-lg font-semibold">{t('screening.feedback.overallPrompt')}</h2>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="h-11 flex-1 gap-2"
                  disabled={isSubmitting}
                  onClick={() => void handleThumbs('up')}
                >
                  <ThumbsUp className="size-5" />
                  {t('screening.feedback.good')}
                </Button>
                <Button
                  variant="outline"
                  className="h-11 flex-1 gap-2"
                  disabled={isSubmitting}
                  onClick={() => void handleThumbs('down')}
                >
                  <ThumbsDown className="size-5" />
                  {t('screening.feedback.bad')}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{t('screening.feedback.disclaimer')}</p>
            </div>
          )}

          {phase === 'notes' && (
            <div className="flex flex-col gap-4 pb-2 pt-1">
              <label className="text-sm font-medium" htmlFor="feedback-notes">
                {t('screening.feedback.notesLabel')}
              </label>
              <textarea
                id="feedback-notes"
                value={notesText}
                onChange={(e) => {
                  setNotesText(e.target.value);
                }}
                placeholder={t('screening.feedback.notesPlaceholder')}
                disabled={isSubmitting}
                maxLength={2000}
                rows={4}
                className="resize-none rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={isSubmitting}
                  onClick={() => {
                    setPhase('done');
                    setSheetOpen(false);
                  }}
                >
                  {t('screening.feedback.skip')}
                </Button>
                <Button
                  className="flex-1"
                  disabled={isSubmitting || !notesText.trim()}
                  onClick={() => void handleSubmitNotes()}
                >
                  {t('screening.feedback.submit')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
